import { useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";

import { DiscrepancyService } from "@services/ingest/discrepancyService";
import type { Correction } from "@models/Discrepancy.model";

interface Props {
  correction: Correction;
  onClose: () => void;
  onReplaced: () => void;
}

/*
    Edit the people on a correction (MPN-2125). Never edited in place: this ends the
    correction and adds one with the new list — same action, same start — that says which
    one it replaced. The old list is not prefilled (ids are decrypted one at a time); paste
    the full new list.
*/
export default function EditPeopleDialog({ correction, onClose, onReplaced }: Props) {
  const [ids, setIds] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const replace = async () => {
    setBusy(true);
    setError(null);
    try {
      await new DiscrepancyService().editCorrectionPeople(correction.id, { ids: ids.split(/[\s,]+/).filter(Boolean) });
      onReplaced();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not replace the people");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>Edit people on correction #{correction.id}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Typography sx={{ fontSize: 13, color: "#64748b" }}>
          This ends correction #{correction.id} ({correction.people.toLocaleString()} people) and adds a new one with
          the people below — same action, same start. Paste the full new list, not just the change.
          {correction.start_file && " It rebuilds again from the same file."}
        </Typography>
        <TextField size="small" multiline minRows={6} label="Ids, one per line or separated by commas"
          value={ids} onChange={(e) => setIds(e.target.value)}
          InputProps={{ sx: { fontFamily: "monospace", fontSize: 12 } }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button variant="contained" onClick={replace} disabled={busy || !ids.trim()} sx={{ textTransform: "none" }}>
          Replace the people
        </Button>
      </DialogActions>
    </Dialog>
  );
}
